/**
 * <weglot-language-switcher> — hydrates the server-rendered language list against Weglot.
 *
 * Weglot serves translations through a SUBDIRECTORY proxy (/de, /fr), so Shopify only ever
 * renders the source-language page. Liquid can emit crawlable links from the theme settings,
 * but it cannot know which language the visitor is actually on, nor Weglot's canonical URL for
 * the current page — both only exist in the `#weglot-data` payload the Weglot app injects.
 * This element corrects the hrefs from that payload, marks the current language, and reconciles
 * the rendered list with the languages Weglot really serves. Without the payload it degrades to
 * the URL prefix and keeps the links Liquid rendered.
 *
 * Public DOM contract:
 * - `data-weglot-original` on the host: source language code (no URL prefix).
 * - `[data-weglot-list]` wraps `[data-weglot-item]` rows, each holding one
 *   `a[data-weglot-code]`.
 * - `[data-weglot-current-label]` / `[data-weglot-trigger]` anywhere in the document are the
 *   footer trigger's language label and the trigger itself.
 */
const readWeglotData = () => {
  const node = document.getElementById('weglot-data');
  if (!node) return null;

  try {
    return JSON.parse(node.textContent);
  } catch {
    return null;
  }
};

const endonymFor = (code) => {
  try {
    const name = new Intl.DisplayNames([code], { type: 'language' }).of(code);
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
};

class WeglotLanguageSwitcher extends HTMLElement {
  #list = null;
  #baseUrls = new Map();
  #onDrawerOpened = null;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.#list = this.querySelector('[data-weglot-list]');
    if (!this.#list) return;

    const urls = readWeglotData()?.allLanguageUrls;
    if (urls) this.#reconcile(urls);
    this.#collectBaseUrls();

    const codes = this.#codes();
    if (codes.length < 2) {
      this.#hide();
      return;
    }

    const current = this.#resolveCurrent(codes);
    this.#markCurrent(current);
    this.#syncTriggerLabel(current);

    // Variant pickers and collection filters rewrite the URL through history.replaceState after
    // this element initialises, so hrefs frozen at init would carry a stale query. The drawer
    // has to be opened before any option is clickable — refreshing there is always in time.
    this.#onDrawerOpened = (event) => {
      if (event.target === this.closest('drawer-component')) this.#refreshHrefs();
    };
    document.addEventListener('drawer:opened', this.#onDrawerOpened);
  }

  disconnectedCallback() {
    if (this.#onDrawerOpened) {
      document.removeEventListener('drawer:opened', this.#onDrawerOpened);
      this.#onDrawerOpened = null;
    }

    this.#baseUrls.clear();
    this.#list = null;
    this.initialized = false;
  }

  // Query-free bases to rebuild hrefs from: Weglot's canonical URLs when the payload is there,
  // otherwise the ones Liquid rendered.
  #collectBaseUrls() {
    this.#list.querySelectorAll('[data-weglot-code]').forEach((link) => {
      const code = link.dataset.weglotCode;
      if (!this.#baseUrls.has(code)) this.#baseUrls.set(code, link.getAttribute('href'));
    });
  }

  #refreshHrefs() {
    this.#list?.querySelectorAll('[data-weglot-code]').forEach((link) => {
      const base = this.#baseUrls.get(link.dataset.weglotCode);
      if (base) link.href = this.#urlFor(base);
    });
  }

  #codes() {
    return Array.from(this.#list.querySelectorAll('[data-weglot-code]'), (link) => link.dataset.weglotCode);
  }

  #reconcile(urls) {
    const available = Object.keys(urls);
    if (!available.length) return;

    const items = Array.from(this.#list.querySelectorAll('[data-weglot-item]'));
    const template = items[0];

    items.forEach((item) => {
      const link = item.querySelector('[data-weglot-code]');
      const code = link?.dataset.weglotCode;

      if (!code || !urls[code]) {
        item.remove();
        return;
      }

      this.#baseUrls.set(code, urls[code]);
      link.href = this.#urlFor(urls[code]);
    });

    if (!template) return;

    available
      .filter((code) => !this.#list.querySelector(`[data-weglot-code="${CSS.escape(code)}"]`))
      .forEach((code) => {
        const item = template.cloneNode(true);
        const link = item.querySelector('[data-weglot-code]');
        if (!link) return;

        link.dataset.weglotCode = code;
        this.#baseUrls.set(code, urls[code]);
        link.href = this.#urlFor(urls[code]);
        link.lang = code;
        link.hreflang = code;
        link.textContent = endonymFor(code);
        link.classList.remove('is-current');
        link.removeAttribute('aria-current');
        this.#list.append(item);
      });
  }

  // Weglot's URLs are canonical and query-free, so carry the current query and hash across —
  // it keeps ?variant=, collection filters and ?preview_theme_id= alive through a switch.
  #urlFor(raw) {
    try {
      const url = new URL(raw, window.location.origin);
      if (!url.search) url.search = window.location.search;
      if (!url.hash) url.hash = window.location.hash;
      return url.toString();
    } catch {
      return raw;
    }
  }

  #resolveCurrent(codes) {
    const original = this.dataset.weglotOriginal || codes[0];

    const translated = document.documentElement.dataset.wgTranslated;
    if (translated && codes.includes(translated)) return translated;

    const [prefix] = window.location.pathname.split('/').filter(Boolean);
    if (prefix && prefix !== original && codes.includes(prefix)) return prefix;

    return codes.includes(original) ? original : codes[0];
  }

  #markCurrent(current) {
    this.#list.querySelectorAll('[data-weglot-code]').forEach((link) => {
      const isCurrent = link.dataset.weglotCode === current;
      link.classList.toggle('is-current', isCurrent);

      if (isCurrent) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
  }

  #syncTriggerLabel(current) {
    const link = this.#list.querySelector(`[data-weglot-code="${CSS.escape(current)}"]`);
    if (!link) return;

    // The trigger renders in the footer section while this element lives in the drawer, so it
    // is unreachable from `this` — the shared data attribute is the seam between the two.
    document.querySelectorAll('[data-weglot-current-label]').forEach((label) => {
      label.textContent = link.textContent.trim();
      label.lang = current;
    });
  }

  #hide() {
    this.hidden = true;
    document.querySelectorAll('[data-weglot-trigger]').forEach((trigger) => {
      trigger.hidden = true;
    });
  }
}

if (!customElements.get('weglot-language-switcher')) {
  customElements.define('weglot-language-switcher', WeglotLanguageSwitcher);
}
